import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Search, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DataTools, useIsOwner } from "./DataTools";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CustomerProfile } from "./CustomerProfile";
import { Empty, Loading, PageHead, Stat, fmtWhen, money, useAdmin } from "./ui";

const COLS = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["bookings", "Bookings"], ["orders", "Orders"], ["spent", "Spent"], ["last_seen", "Last activity"]];

const Head = ({ sort, setSort }) => (
  <thead><tr>
    {[...COLS, ["", ""]].map(([k, l]) => (
      <th key={k || "actions"}>
        {k && <button type="button" onClick={() => setSort({ key: k, dir: sort.key === k && sort.dir === "asc" ? "desc" : "asc" })} data-testid={`customers-sort-${k}`}>
          {l} {sort.key === k && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
        </button>}
      </th>
    ))}
  </tr></thead>
);

const matches = (c, t, digits) => !t || c.name.toLowerCase().includes(t) || c.email.includes(t) || (digits && c.phone.replace(/\D/g, "").includes(digits)) || c.tags.some((x) => x.toLowerCase().includes(t));
const sortValue = (c, key) => (typeof c[key] === "number" ? c[key] : String(c[key] || "").toLowerCase());

const useCustomerRows = (data, q, sort) => useMemo(() => {
  const t = q.trim().toLowerCase();
  const digits = t.replace(/\D/g, "");
  const dir = sort.dir === "asc" ? 1 : -1;
  return (data || []).filter((c) => matches(c, t, digits)).sort((a, b) => {
    const [x, y] = [sortValue(a, sort.key), sortValue(b, sort.key)];
    return (x > y ? 1 : x < y ? -1 : 0) * dir;
  });
}, [data, q, sort]);

const useDeleteCustomer = () => {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name || c.email} and ALL their bookings, orders, payments, messages, reviews and subscriptions? This cannot be undone.`)) return;
    try { await api.delete(`/admin/customers?email=${encodeURIComponent(c.email)}`); toast.success("Customer deleted"); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return { refresh, remove };
};

const Row = ({ c, isOwner, onOpen, onDelete }) => (
  <tr data-testid={`customer-row-${c.email}`}>
    <td><button type="button" className="cu-name" onClick={() => onOpen(c.email)} data-testid={`customer-open-${c.email}`}>{c.name || c.email.split("@")[0]}</button>
      {c.tags.length > 0 && <small className="cu-row-tags">{c.tags.join(" · ")}</small>}</td>
    <td>{c.email}</td><td>{c.phone || "—"}</td><td>{c.bookings}</td><td>{c.orders}</td><td>{money(c.spent)}</td><td>{fmtWhen(c.last_seen)}</td>
    <td>{isOwner && <button type="button" className="ad-icon-btn" onClick={() => onDelete(c)} aria-label="Delete customer" data-testid={`customer-delete-${c.email}`}><Trash2 size={14} /></button>}</td>
  </tr>
);

const Summary = ({ data }) => (
  <div className="ad-stats" data-testid="customers-summary">
    <Stat label="Customers" value={data.length} testId="customers-stat-total" />
    <Stat label="Booked a service" value={data.filter((c) => c.bookings).length} testId="customers-stat-booked" />
    <Stat label="Bought products" value={data.filter((c) => c.orders).length} testId="customers-stat-ordered" />
    <Stat label="Total spent" value={money(data.reduce((s, c) => s + c.spent, 0))} testId="customers-stat-spent" />
  </div>
);

export default function Customers() {
  const { data } = useAdmin("customers", "/admin/customers");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "last_seen", dir: "desc" });
  const [open, setOpen] = useState(null);
  const rows = useCustomerRows(data, q, sort);
  const isOwner = useIsOwner();
  const { refresh, remove } = useDeleteCustomer();
  return (
    <div data-testid="admin-customers">
      <PageHead eyebrow="Clients" title="Customers" />
      {!data ? <Loading /> : (
        <>
          <Summary data={data} />
          <div className="cu-tools">
            <label className="cu-search"><Search size={16} /><input type="search" placeholder="Search name, email, phone or tag" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search-input" /></label>
          </div>
          <DataTools section="customers" label="customers (with no activity since that date)" exportUrl="/admin/customers/export" filename="waxmoi-customers.xlsx" onPurged={refresh} />
          {rows.length === 0 ? <Empty>No customers match.</Empty> : (
            <div className="cu-table-wrap">
              <table className="cu-table" data-testid="customers-table">
                <Head sort={sort} setSort={setSort} />
                <tbody>
                  {rows.map((c) => <Row key={c.email} c={c} isOwner={isOwner} onOpen={setOpen} onDelete={remove} />)}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="ad-msg-dialog" aria-describedby={undefined} data-testid="customer-dialog">{open && <CustomerProfile email={open} />}</DialogContent>
      </Dialog>
    </div>
  );
}
